import { useEffect, useState, useMemo } from "react";
import { api, formatINR } from "@/lib/api";
import { PageHeader, Card, StatusBadge, EmptyState } from "@/components/ui-helpers";
import { BranchFilter } from "@/components/BranchFilter";
import { Plus, X, Info, Search, Download, Upload, FileDown, AlertCircle,
  Warehouse as WarehouseIcon, ShieldAlert, Wrench, CheckCircle2, History,
  Pencil, Trash2 } from "lucide-react";
import { inputCls, Field } from "./Projects";
import { SearchableSelect } from "@/components/SearchableSelect";
import { toast, Toaster } from "sonner";
import { usePaginatedList } from "@/hooks/usePaginatedList";
import { PaginationControls } from "@/components/PaginationControls";
import { useFormTab } from "@/hooks/useFormTab";
import UserLabel, { formatUser, useUserDirectory } from "@/components/UserLabel";
import { useAuth } from "@/contexts/AuthContext";
import { BulkAssignWarehouseModal } from "@/components/BulkAssignWarehouseModal";

const STATUS_TABS = ["All", "Available", "Assigned", "In Repair", "Scrapped", "Transferred"];

export default function Assets() {
  useUserDirectory();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [members, setMembers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [filterBranch, setFilterBranch] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [statusTab, setStatusTab] = useState("All");
  const [filterWarehouse, setFilterWarehouse] = useState("");
  const [facets, setFacets] = useState(null);
  const [qDebounced, setQDebounced] = useState("");
  const [allRepairs, setAllRepairs] = useState([]);
  // Bulk upload — single template, two modes (create_only / update_existing)
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkMode, setBulkMode] = useState("create_only");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);   // { ok, created, updated } on success
  const [bulkErrors, setBulkErrors] = useState(null);   // { message, errors[] } on 422
  // Sprint 4.7 — warehouse coverage banner + bulk-assign overlay
  const [coverage, setCoverage] = useState(null);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);

  // Server-side pagination-safe filters (Jun-2026): status / warehouse /
  // category / search all resolve on the server across the FULL asset base.
  useEffect(() => {
    const t = setTimeout(() => setQDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);
  const params = useMemo(() => ({
    ...(filterBranch ? { branch_id: filterBranch } : {}),
    ...(statusTab !== "All" ? { status: statusTab } : {}),
    ...(filterWarehouse ? { warehouse_id: filterWarehouse } : {}),
    ...(filterCategory ? { category: filterCategory } : {}),
    ...(qDebounced ? { q: qDebounced } : {}),
  }), [filterBranch, statusTab, filterWarehouse, filterCategory, qDebounced]);
  const { items: list, total, page, pageSize, loading, setPage, setPageSize, reload } = usePaginatedList("/assets", params);
  const openFormTab = useFormTab();

  const load = async () => {
    const [m, p, r] = await Promise.all([api.get("/members/_pickable"), api.get("/projects-picker"), api.get("/asset-repairs").catch(() => ({ data: [] }))]);
    setMembers(m.data); setProjects(p.data); setAllRepairs(r.data || []);
    reload();
    // Sprint 4.7 — coverage banner (silently tolerate 403/4xx)
    const qs = filterBranch ? `?branch_id=${filterBranch}` : "";
    api.get(`/inventory/warehouse-coverage${qs}`)
       .then(rr => setCoverage(rr.data))
       .catch(() => setCoverage(null));
    api.get(`/assets-facets${qs}`)
       .then(rr => setFacets(rr.data))
       .catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  // Refresh coverage when the branch filter changes (the coverage banner
  // is branch-scoped).
  useEffect(() => {
    const qs = filterBranch ? `?branch_id=${filterBranch}` : "";
    api.get(`/inventory/warehouse-coverage${qs}`)
       .then(rr => setCoverage(rr.data))
       .catch(() => setCoverage(null));
    api.get(`/assets-facets${qs}`)
       .then(rr => setFacets(rr.data))
       .catch(() => setFacets(null));
  }, [filterBranch]);

  const openNew = () => openFormTab({
    path: "/assets/new",
    label: "New Asset",
    icon: Plus,
    parent: "/assets",
    onSaved: load,
    render: ({ close }) => <AssetModal asset={null} members={members} projects={projects} onClose={close} onSaved={close} />,
  });
  const openEdit = (a) => openFormTab({
    path: `/assets/edit/${a.id}`,
    label: `Edit · ${a.asset_code || a.name || "Asset"}`,
    parent: "/assets",
    onSaved: load,
    render: ({ close }) => <AssetModal asset={a} members={members} projects={projects} onClose={close} onSaved={close} />,
  });

  async function downloadBulkTemplate() {
    try {
      const r = await api.get("/assets-bulk-template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "assets_bulk_template.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Template download failed");
    }
  }

  async function submitBulkUpload() {
    if (!bulkFile) { toast.error("Please select a file"); return; }
    setBulkBusy(true);
    setBulkErrors(null); setBulkResult(null);
    const fd = new FormData();
    fd.append("file", bulkFile);
    fd.append("mode", bulkMode);
    try {
      const r = await api.post("/assets-bulk-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBulkResult(r.data);
      const noun = bulkMode === "create_only" ? "created" : `${r.data.created} created · ${r.data.updated} updated`;
      toast.success(`Bulk upload complete · ${noun}`);
      reload();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.errors) {
        // 422 fail-fast — show the per-row error report
        setBulkErrors(d);
      } else {
        toast.error(typeof d === "string" ? d : "Upload failed");
      }
    } finally {
      setBulkBusy(false);
    }
  }

  function closeBulkModal() {
    if (bulkBusy) return;
    setBulkOpen(false);
    setBulkFile(null);
    setBulkErrors(null);
    setBulkResult(null);
  }

  // Find the latest open RRN per asset (used for the "In Repair" status chip on the list)
  const openRrnByAsset = useMemo(() => {
    const m = {};
    for (const r of allRepairs) {
      if (r.marks_in_repair && !r.repair_completed_at) {
        const prev = m[r.asset_id];
        if (!prev || (r.voucher_date || "") > (prev.voucher_date || "")) m[r.asset_id] = r;
      }
    }
    return m;
  }, [allRepairs]);

  const categories = useMemo(() => {
    if (facets?.categories?.length) return facets.categories;
    const s = new Set();
    list.forEach(a => a.category && s.add(a.category));
    return Array.from(s).sort();
  }, [facets, list]);

  // Tab badges come from the SERVER facets (the list is paginated — counting
  // the loaded page under-reported, e.g. "In Repair 0" with 478 assets).
  const statusCounts = useMemo(() => {
    if (facets) {
      const c = { All: facets.total || 0 };
      STATUS_TABS.slice(1).forEach(s => { c[s] = facets.status_counts?.[s] || 0; });
      return c;
    }
    const c = { All: list.length };
    STATUS_TABS.slice(1).forEach(s => { c[s] = 0; });
    list.forEach(a => { c[a.status] = (c[a.status] || 0) + 1; });
    return c;
  }, [facets, list]);

  // Status / warehouse / category / search now filter SERVER-side via params.
  const filtered = list;

  async function handleDelete(id) {
    if (!window.confirm("Delete this asset? This is irreversible.")) return;
    try { await api.delete(`/assets/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }



  async function sendToRepair(a) {
    openFormTab({
      path: `/assets/${a.id}/send-to-repair`,
      label: `Send to Repair · ${a.asset_code || a.name}`,
      parent: "/assets",
      onSaved: load,
      render: ({ close }) => <SendToRepairModal asset={a} onClose={close} onSaved={close} />,
    });
  }

  async function markReady(a) {
    openFormTab({
      path: `/assets/${a.id}/mark-ready`,
      label: `Mark Ready · ${a.asset_code || a.name}`,
      parent: "/assets",
      onSaved: load,
      render: ({ close }) => <MarkReadyModal asset={a} members={members} onClose={close} onSaved={close} />,
    });
  }

  function openRepairReport(a) {
    openFormTab({
      path: `/assets/${a.id}/history`,
      label: `Asset History · ${a.asset_code || a.name}`,
      parent: "/assets",
      render: ({ close }) => <RepairReportModal asset={a} onClose={close} />,
    });
  }

  return (
    <div>
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="Asset Management"
        title="Assets"
        subtitle="Track company assets with location, member assignment and movement history."
        actions={
          <div className="flex items-center gap-2">
            <button data-testid="assets-bulk-template-btn" onClick={downloadBulkTemplate}
                    className="border border-slate-300 hover:border-slate-700 hover:bg-slate-50 text-slate-700 px-3 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2"
                    title="Download Excel template for bulk asset upload">
              <FileDown size={14} /> Template
            </button>
            <button data-testid="assets-bulk-upload-btn" onClick={() => setBulkOpen(true)}
                    className="border border-slate-300 hover:border-slate-700 hover:bg-slate-50 text-slate-700 px-3 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2"
                    title="Bulk upload + assign assets via template">
              <Upload size={14} /> Bulk Upload
            </button>
            {isAdmin && (
              <button data-testid="assets-bulk-assign-warehouse-btn"
                      onClick={() => setBulkAssignOpen(true)}
                      className="border border-slate-300 hover:border-slate-700 hover:bg-slate-50 text-slate-700 px-3 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2"
                      title="Bulk-tag existing asset records to a warehouse">
                <WarehouseIcon size={14} /> Assign Warehouse
              </button>
            )}
            <button data-testid="add-asset-btn" onClick={openNew} className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
              <Plus size={14} /> New Asset
            </button>
          </div>
        }
      />

      {/* Sprint 4.7 — coverage banner: surfaces when legacy assets lack a warehouse_id. */}
      {coverage && isAdmin && coverage.assets?.missing > 0 && (
        <div className="bg-amber-50 border border-amber-300 px-4 py-3 mb-3 flex items-center gap-3 flex-wrap"
             data-testid="assets-coverage-banner">
          <ShieldAlert size={16} className="text-amber-700 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-amber-800">Warehouse tagging incomplete</div>
            <div className="text-xs text-amber-900 mt-0.5">
              <strong className="tabular-nums">{coverage.assets.missing}</strong> of {coverage.assets.total} assets have no warehouse linked.
            </div>
          </div>
          <button data-testid="assets-coverage-banner-fix"
            onClick={() => setBulkAssignOpen(true)}
            className="bg-amber-700 hover:bg-amber-800 text-white px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5">
            <WarehouseIcon size={12} /> Fix it now
          </button>
        </div>
      )}

      {/* Status tabs */}
      <div className="flex gap-1 mb-3 border-b border-slate-200 flex-wrap">
        {STATUS_TABS.map(s => (
          <button key={s} data-testid={`status-tab-${s.toLowerCase().replace(/\s/g, '-')}`}
            onClick={() => setStatusTab(s)}
            className={`px-5 py-2.5 text-xs uppercase tracking-wider font-bold border-b-2 -mb-px flex items-center gap-2 ${
              statusTab === s ? "border-[#F97316] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}>
            {s}
            <span className={`text-[10px] px-1.5 py-0.5 ${statusTab === s ? "bg-[#F97316] text-white" : "bg-slate-100 text-slate-600"}`}>
              {statusCounts[s] || 0}
            </span>
          </button>
        ))}
      </div>

      {/* Filter bar */}
      <div className="mb-3 flex items-center gap-3 flex-wrap">
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="search-input" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search code / name / serial / location…"
            className="border border-slate-300 bg-white pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-[#F97316] min-w-[300px]" />
        </div>
        <select data-testid="assets-category-filter" value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
          className="border border-slate-300 bg-white px-3 py-2 text-sm">
          <option value="">All categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Branch</span>
        <BranchFilter value={filterBranch} onChange={setFilterBranch} testid="assets-branch-filter" />
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Warehouse</span>
        <select data-testid="assets-warehouse-filter" value={filterWarehouse} onChange={e => setFilterWarehouse(e.target.value)}
          className="border border-slate-300 bg-white px-3 py-2 text-sm max-w-[240px]">
          <option value="">All warehouses</option>
          <option value="__none__">No warehouse{facets?.untagged_warehouse_count ? ` (${facets.untagged_warehouse_count})` : ""}</option>
          {(facets?.warehouses || []).map(w => (
            <option key={w.id} value={w.id}>{w.warehouse_code ? `${w.warehouse_code} — ` : ""}{w.warehouse_name}</option>
          ))}
        </select>
        <div className="ml-auto text-xs text-slate-500">
          <span className="text-slate-400 mr-1">Showing:</span>
          <span className="font-bold text-slate-800 tabular">{filtered.length}</span>
          <span className="text-slate-400 mx-1">/</span>
          <span className="text-slate-600 tabular">{total}</span>
        </div>
      </div>

      <div className="bg-amber-50/60 border border-amber-200 px-4 py-2 mb-3 flex items-start gap-2">
        <Info size={14} className="text-amber-700 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-900">
          Asset records are <strong>auto-generated</strong> on Purchase Invoice approval — one per unit qty of an
          <em> Asset</em>-type item, with codes <code className="font-mono">AST/&lt;ITEM&gt;-NNN</code> (prefix configurable in <em>Settings → Numbering Series → Asset</em>) and branch/project snapshotted from the source PI.
          To assign or return an asset, use <strong>Issue Voucher</strong> / <strong>Return Voucher</strong>; for inter-branch moves use <strong>Stock Transfer → Asset Transfer</strong>.
        </p>
      </div>

      {filtered.length === 0 ? <EmptyState title="No assets yet" /> : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="assets-table">
              <thead className="bg-slate-50 border-b-2 border-slate-200">
                <tr>{["Code", "Asset", "Branch", "Warehouse", "Category", "Serial", "Location", "Assigned To", "Value", "Status", ""].map(h =>
                  <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-4">{h}</th>
                )}</tr>
              </thead>
              <tbody>
                {filtered.map(a => (
                  <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="py-3 px-4 text-xs font-mono font-bold text-slate-900">{a.asset_code}</td>
                    <td className="py-3 px-4 text-sm text-slate-800">{a.name}{a.source_pi_number && <div className="text-[10px] text-slate-400 font-mono">PI {a.source_pi_number}</div>}</td>
                    <td className="py-3 px-4 text-xs text-slate-600">
                      {a.branch_name
                        ? a.branch_name
                        : (a.branch_id
                            ? <span className="font-mono text-slate-500">{a.branch_id.slice(0, 8)}…</span>
                            : <span data-testid={`asset-no-branch-${a.id}`}
                                    title="No branch tagged — visible to every branch's Issue Voucher picker. Edit the asset to assign a branch."
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200 rounded">
                                ⚠ no branch
                              </span>)}
                    </td>
                    <td className="py-3 px-4 text-xs text-slate-700" data-testid={`asset-wh-${a.id}`}>
                      {a.warehouse_name
                        ? <span className="font-bold">{a.warehouse_name}</span>
                        : <span className="text-amber-600">—</span>}
                    </td>
                    <td className="py-3 px-4 text-xs uppercase text-slate-600">{a.category}</td>
                    <td className="py-3 px-4 text-xs text-slate-500">{a.serial_number || "—"}</td>
                    <td className="py-3 px-4 text-sm text-slate-700">{a.current_location || "—"}</td>
                    <td className="py-3 px-4 text-sm text-slate-700">{a.assigned_to_member_name || <span className="text-slate-400">—</span>}</td>
                    <td className="py-3 px-4 text-sm tabular">{formatINR(a.purchase_value)}</td>
                    <td className="py-3 px-4">
                      <StatusBadge status={a.status} />
                      {a.status === "In Repair" && openRrnByAsset[a.id] && (
                        <div className="text-[10px] font-mono font-bold text-[#F97316] mt-1" data-testid={`asset-rrn-${a.id}`}>RRN {openRrnByAsset[a.id].voucher_number}</div>
                      )}
                    </td>
                    <td className="py-3 px-4 text-xs whitespace-nowrap">
                      <div className="inline-flex items-center gap-0.5">
                      {(a.status === "Available" || a.status === "Assigned") && (
                        <button data-testid={`send-to-repair-${a.id}`} onClick={() => sendToRepair(a)}
                          title="Send to Repair"
                          className="p-1.5 text-amber-700 hover:bg-amber-50 hover:text-amber-900 rounded transition">
                          <Wrench size={14} />
                        </button>
                      )}
                      {a.status === "In Repair" && (
                        <button data-testid={`mark-ready-${a.id}`} onClick={() => markReady(a)}
                          title="Mark Ready (return from repair)"
                          className="p-1.5 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-900 rounded transition">
                          <CheckCircle2 size={14} />
                        </button>
                      )}
                      <button data-testid={`repair-report-${a.id}`} onClick={() => openRepairReport(a)}
                        title="History (repair + movement audit)"
                        className="p-1.5 text-blue-700 hover:bg-blue-50 hover:text-blue-900 rounded transition">
                        <History size={14} />
                      </button>
                      <button data-testid={`edit-asset-${a.id}`} onClick={() => openEdit(a)}
                        title="Edit asset"
                        className="p-1.5 text-slate-600 hover:bg-slate-100 hover:text-slate-900 rounded transition">
                        <Pencil size={14} />
                      </button>
                      <button data-testid={`delete-asset-${a.id}`} onClick={() => handleDelete(a.id)}
                        title="Delete asset"
                        className="p-1.5 text-red-600 hover:bg-red-50 hover:text-red-900 rounded transition">
                        <Trash2 size={14} />
                      </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationControls page={page} pageSize={pageSize} total={total} loading={loading} onPage={setPage} onPageSize={setPageSize} />
        </Card>
      )}

      {/* Bulk Upload modal — single template, two modes */}
      {bulkOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto"
             onClick={closeBulkModal}
             data-testid="assets-bulk-modal">
          <div className="bg-white max-w-2xl w-full p-5 my-8 border-t-4 border-slate-700"
               onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg font-black text-slate-900 flex items-center gap-2">
                <Upload size={18} /> Bulk Upload &amp; Assign Assets
              </h3>
              <button onClick={closeBulkModal} disabled={bulkBusy} data-testid="bulk-close-btn"><X size={16} /></button>
            </div>

            <div className="text-[11px] uppercase tracking-wider font-bold text-slate-600 bg-slate-50 border border-slate-200 p-2 mb-3">
              Manual import flow · The auto-asset-creation from approved Purchase Invoices is unaffected.
            </div>

            <label className="block mb-3">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600 block mb-1">Upload Mode *</span>
              <select value={bulkMode} onChange={e => setBulkMode(e.target.value)}
                      disabled={bulkBusy}
                      data-testid="bulk-mode-select"
                      className="w-full text-sm px-3 py-2 border border-slate-300 focus:border-slate-700 focus:outline-none">
                <option value="create_only">Create only — fail if any Asset Code already exists</option>
                <option value="update_existing">Update existing — overwrite Asset Codes that already exist</option>
              </select>
            </label>

            <label className="block mb-3">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600 block mb-1">Excel File (.xlsx) *</span>
              <input type="file" accept=".xlsx,.xls"
                     onChange={e => { setBulkFile(e.target.files?.[0] || null); setBulkErrors(null); setBulkResult(null); }}
                     disabled={bulkBusy}
                     data-testid="bulk-file-input"
                     className="block w-full text-xs file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-slate-700 file:text-white file:text-xs file:font-bold file:uppercase file:tracking-wider hover:file:bg-slate-900 cursor-pointer" />
              <span className="text-[10px] text-slate-500 mt-1 block">
                Max 1000 rows. Use the official template (Template button above) for the required columns.
              </span>
            </label>

            {/* Per-row error report */}
            {bulkErrors && (
              <div className="mb-3 border border-rose-200 bg-rose-50 p-3" data-testid="bulk-errors-panel">
                <div className="flex items-start gap-2 mb-2">
                  <AlertCircle size={16} className="text-rose-600 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-rose-800">
                    <div className="font-bold uppercase tracking-wider text-[11px] mb-1">Upload rejected</div>
                    <div className="leading-relaxed">{bulkErrors.message}</div>
                    <div className="mt-1 text-[10px] text-rose-700">
                      Rows in file: {bulkErrors.rows_total} · Rows that would have passed: {bulkErrors.rows_valid}
                    </div>
                  </div>
                </div>
                <div className="max-h-60 overflow-y-auto border border-rose-200 bg-white">
                  <table className="w-full text-[11px]">
                    <thead className="bg-rose-100 text-rose-900 sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1 font-bold uppercase tracking-wider text-[10px]">Row</th>
                        <th className="text-left px-2 py-1 font-bold uppercase tracking-wider text-[10px]">Asset Code</th>
                        <th className="text-left px-2 py-1 font-bold uppercase tracking-wider text-[10px]">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkErrors.errors.map((e, i) => (
                        <tr key={i} className="border-t border-rose-100" data-testid={`bulk-err-row-${i}`}>
                          <td className="px-2 py-1 font-mono text-slate-600">{e.row}</td>
                          <td className="px-2 py-1 font-mono text-slate-700">{e.asset_code || "—"}</td>
                          <td className="px-2 py-1 text-rose-800">{e.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Success summary */}
            {bulkResult && (
              <div className="mb-3 border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800" data-testid="bulk-success-panel">
                <div className="font-bold uppercase tracking-wider text-[11px] mb-1">Upload successful</div>
                {bulkResult.rows_total} row(s) processed · {bulkResult.created} created · {bulkResult.updated} updated.
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button onClick={downloadBulkTemplate} disabled={bulkBusy}
                      className="px-3 py-2 border border-slate-300 hover:bg-slate-50 text-xs uppercase tracking-wider font-bold inline-flex items-center gap-1"
                      data-testid="bulk-template-link">
                <FileDown size={11} /> Template
              </button>
              <button onClick={closeBulkModal} disabled={bulkBusy}
                      className="px-3 py-2 border border-slate-300 text-xs uppercase tracking-wider font-bold"
                      data-testid="bulk-cancel-btn">Close</button>
              <button onClick={submitBulkUpload} disabled={bulkBusy || !bulkFile}
                      data-testid="bulk-submit-btn"
                      className="bg-slate-700 hover:bg-slate-900 disabled:bg-slate-300 disabled:cursor-not-allowed text-white px-4 py-2 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1">
                <Upload size={11} /> {bulkBusy ? "Uploading…" : "Upload"}
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkAssignOpen && (
        <BulkAssignWarehouseModal
          mode="assets"
          defaultBranchId={filterBranch || ""}
          onClose={() => setBulkAssignOpen(false)}
          onDone={() => { setBulkAssignOpen(false); load(); }}
        />
      )}

    </div>
  );
}

function AssetModal({ asset, members, projects, onClose, onSaved }) {
  const [form, setForm] = useState(asset || {
    asset_code: "", name: "", category: "General", serial_number: "",
    make: "", warranty_until: "",
    purchase_date: "", purchase_value: 0, salvage_value: 0, current_location: "",
    status: "Available", project_id: "", remarks: "",
    depreciation_method: "SLM", useful_life_years: 0, depreciation_rate: 0,
    // Sprint 4.3 — Branch + Warehouse stamping (mandatory on manual create)
    branch_id: "", branch_name: "",
    warehouse_id: "", warehouse_name: "",
  });
  const [saving, setSaving] = useState(false);
  const [branches, setBranches] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const set = (k, v) => setForm({ ...form, [k]: v });

  useEffect(() => {
    api.get("/branches").then(r => setBranches(r.data || []));
  }, []);

  // Sprint 4.3 — Branch → Warehouse cascade
  useEffect(() => {
    if (!form.branch_id) { setWarehouses([]); return; }
    api.get(`/warehouses/picker?branch_id=${form.branch_id}`).then(r => {
      const wlist = Array.isArray(r.data) ? r.data : [];
      setWarehouses(wlist);
      const cur = wlist.find(x => x.id === form.warehouse_id);
      if (!cur) {
        const dflt = wlist.find(x => x.is_default) || wlist[0];
        setForm(p => ({ ...p,
          warehouse_id:   dflt?.id || "",
          warehouse_name: dflt?.warehouse_name || "",
        }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.branch_id]);

  async function save(e) {
    e.preventDefault();
    if (!asset) {
      // New asset — Branch + Warehouse are mandatory (Sprint 4.3 Phase C).
      if (!form.branch_id)    { toast.error("Branch is mandatory on a new Asset."); return; }
      if (!form.warehouse_id) { toast.error("Warehouse is mandatory. Pick a warehouse for the selected branch."); return; }
    }
    setSaving(true);
    try {
      const proj = projects.find(p => p.id === form.project_id);
      const br = branches.find(b => b.id === form.branch_id);
      const payload = {
        ...form,
        branch_name: br?.branch_name || form.branch_name || "",
        project_name: proj?.project_name || "",
        purchase_value: Number(form.purchase_value || 0),
        salvage_value: Number(form.salvage_value || 0),
        useful_life_years: Number(form.useful_life_years || 0),
        depreciation_rate: Number(form.depreciation_rate || 0),
      };
      if (asset) await api.put(`/assets/${asset.id}`, payload);
      else await api.post("/assets", payload);
      toast.success("Saved"); onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  }

  return (
    <div className="bg-white -mt-6 lg:-mt-10 -mx-4 sm:-mx-6 lg:-mx-10 min-h-[calc(100vh-38px)]">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-[38px] bg-white z-10 shadow-sm">
          <div>
            <h2 className="font-display text-xl font-bold">{asset ? "Edit" : "New"} Asset</h2>
            {asset?.asset_code && <div className="text-[10px] font-mono text-slate-400 mt-0.5">{asset.asset_code}</div>}
          </div>
          <button onClick={onClose} data-testid="asset-modal-close"><X size={18} /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-5 max-w-[1400px] mx-auto">
          {/* Identity */}
          <div>
            <h3 className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">Identity</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Asset Code *"><input data-testid="asset-code" required value={form.asset_code} onChange={e => set("asset_code", e.target.value)} className={inputCls} /></Field>
              <Field label="Name *"><input data-testid="asset-name" required value={form.name} onChange={e => set("name", e.target.value)} className={inputCls} /></Field>
              <Field label="Category">
                <select data-testid="asset-category" value={form.category} onChange={e => set("category", e.target.value)} className={inputCls}>
                  {["General", "IT Equipment", "Site Equipment", "Tools", "Vehicle", "Furniture", "Machinery"].map(c => <option key={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Make / Brand">
                <input data-testid="asset-make" value={form.make || ""} onChange={e => set("make", e.target.value)} placeholder="e.g. Dell, Cummins, Honda" className={inputCls} />
              </Field>
              <Field label="Serial Number"><input value={form.serial_number || ""} onChange={e => set("serial_number", e.target.value)} className={inputCls} /></Field>
              <Field label="Warranty Until">
                <input data-testid="asset-warranty-until" type="date" value={form.warranty_until || ""} onChange={e => set("warranty_until", e.target.value)} className={inputCls} />
              </Field>
            </div>
          </div>

          {/* Financial */}
          <div className="border-t border-slate-200 pt-4">
            <h3 className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">Financial</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Purchase Date"><input type="date" value={form.purchase_date || ""} onChange={e => set("purchase_date", e.target.value)} className={inputCls} /></Field>
              <Field label="Purchase Value (₹)"><input data-testid="asset-pv" type="number" step="0.01" value={form.purchase_value} onChange={e => set("purchase_value", e.target.value)} className={inputCls} /></Field>
              <Field label="Salvage Value (₹)"><input data-testid="asset-salvage" type="number" step="0.01" value={form.salvage_value || 0} onChange={e => set("salvage_value", e.target.value)} className={inputCls} placeholder="Residual / scrap value" /></Field>
            </div>
          </div>

          {/* Depreciation */}
          <div className="border-t border-slate-200 pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Depreciation</h3>
              <span className="text-[10px] text-slate-400">Run from Assets → <strong>Depreciation</strong> page (Monthly / Yearly · idempotent)</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Method">
                <select data-testid="asset-dep-method" value={form.depreciation_method || "SLM"} onChange={e => set("depreciation_method", e.target.value)} className={inputCls}>
                  <option value="SLM">SLM — Straight Line</option>
                  <option value="WDV">WDV — Written Down Value</option>
                </select>
              </Field>
              <Field label={`Useful Life (Years)${(form.depreciation_method || "SLM") === "SLM" ? " *" : ""}`}>
                <input data-testid="asset-useful-life" type="number" step="0.5" value={form.useful_life_years || 0}
                  onChange={e => set("useful_life_years", e.target.value)} className={inputCls}
                  disabled={(form.depreciation_method || "SLM") === "WDV"} />
              </Field>
              <Field label={`Depreciation Rate %${(form.depreciation_method || "SLM") === "WDV" ? " *" : ""}`}>
                <input data-testid="asset-dep-rate" type="number" step="0.01" value={form.depreciation_rate || 0}
                  onChange={e => set("depreciation_rate", e.target.value)} className={inputCls}
                  disabled={(form.depreciation_method || "SLM") === "SLM"} />
              </Field>
            </div>
            <p className="text-[11px] text-slate-500 mt-2 italic">
              {(form.depreciation_method || "SLM") === "SLM"
                ? <>SLM monthly dep = <code className="font-mono">(Purchase Value − Salvage) ÷ Useful Life ÷ 12</code></>
                : <>WDV monthly dep = <code className="font-mono">Opening Net Block × Rate% ÷ 12</code></>}
            </p>
          </div>

          {/* Assignment & status */}
          <div className="border-t border-slate-200 pt-4">
            <h3 className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">Location & Assignment</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Branch *">
                <select data-testid="asset-branch" required value={form.branch_id || ""}
                  onChange={e => set("branch_id", e.target.value)} className={inputCls}>
                  <option value="">Select branch…</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.branch_name}</option>)}
                </select>
              </Field>
              <Field label="Warehouse *">
                <select data-testid="asset-warehouse" required disabled={!form.branch_id}
                  value={form.warehouse_id || ""}
                  onChange={e => {
                    const w = warehouses.find(x => x.id === e.target.value);
                    setForm(p => ({ ...p,
                      warehouse_id: e.target.value, warehouse_name: w?.warehouse_name || "",
                    }));
                  }}
                  className={inputCls}>
                  <option value="">
                    {form.branch_id ? (warehouses.length ? "Select warehouse…" : "No warehouse available") : "Pick a branch first"}
                  </option>
                  {warehouses.map(w => (
                    <option key={w.id} value={w.id}>
                      {w.warehouse_code} — {w.warehouse_name}{w.is_default ? " · Default" : ""}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Current Location"><input value={form.current_location || ""} onChange={e => set("current_location", e.target.value)} className={inputCls} /></Field>
              <Field label="Status">
                <select value={form.status} onChange={e => set("status", e.target.value)} className={inputCls}>
                  {["Available", "Assigned", "In Repair", "Scrapped", "Transferred"].map(s => <option key={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Project">
                <select value={form.project_id || ""} onChange={e => set("project_id", e.target.value)} className={inputCls}>
                  <option value="">—</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.project_code} · {p.project_name}</option>)}
                </select>
              </Field>
              <Field label="Remarks" full><textarea rows={2} value={form.remarks || ""} onChange={e => set("remarks", e.target.value)} className={inputCls} /></Field>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
            <button type="button" onClick={onClose} className="px-5 py-2.5 border border-slate-300 text-sm font-bold uppercase tracking-wider">Cancel</button>
            <button data-testid="save-asset-btn" disabled={saving} type="submit" className="px-5 py-2.5 bg-[#F97316] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#EA580C] disabled:opacity-60">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
    </div>
  );
}


// ============== Send to Repair Modal ==============
function SendToRepairModal({ asset, onClose, onSaved }) {
  const [vendors, setVendors] = useState([]);
  const [vendorId, setVendorId] = useState("");
  const [vendorName, setVendorName] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/vendors/picker").then(r => setVendors(r.data || [])); }, []);

  async function save(e) {
    e.preventDefault();
    if (!vendorId || !vendorName) { toast.error("Pick a vendor from the master"); return; }
    if (!reason.trim()) { toast.error("Reason is mandatory"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/assets/${asset.id}/send-to-repair`, {
        vendor_id: vendorId, vendor_name: vendorName, reason: reason.trim(),
      });
      toast.success(`${asset.asset_code} → In Repair · RRN ${data.voucher_number}`);
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  }

  return (
    <div className="bg-white -mt-6 lg:-mt-10 -mx-4 sm:-mx-6 lg:-mx-10 min-h-[calc(100vh-38px)]" data-testid="send-to-repair-modal">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-[38px] bg-white z-10 shadow-sm">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Send Asset to Repair</div>
            <h2 className="font-display text-xl font-bold mt-0.5">{asset.asset_code} · {asset.name}</h2>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-4">
          <Field label="Vendor *">
            <SearchableSelect
              testid="str-vendor-search"
              value={vendorId}
              onChange={(id, row) => { setVendorId(id || ""); setVendorName(row?.name || ""); }}
              items={vendors}
              getLabel={(v) => `${v.vendor_code ? v.vendor_code + " — " : ""}${v.name}`}
              getSubLabel={(v) => [v.gst_number, v.branch_name].filter(Boolean).join(" · ")}
              getSearchText={(v) => `${v.vendor_code || ""} ${v.name || ""} ${v.gst_number || ""}`}
              placeholder="Select vendor — search by code or name…"
              searchPlaceholder={`Search ${vendors.length} vendors by code / name / GSTIN…`}
            />
            {vendorId && <div className="text-[10px] text-emerald-700 mt-1">✓ Selected: <span className="font-bold">{(vendors.find(v => v.id === vendorId) || {}).vendor_code || ""} — {vendorName}</span></div>}
          </Field>
          <Field label="Reason *">
            <textarea data-testid="str-reason" rows={3} value={reason} onChange={e => setReason(e.target.value)}
              className={inputCls} placeholder="Nature of fault, parts needing replacement, etc."
              required />
          </Field>
          <div className="bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-900">
            On post: a Repair Request Number (RRN) is auto-generated, asset → <strong>In Repair</strong>, zero-cost repair voucher created. Cost gets posted later via a Purchase Invoice (Category = Asset Repair) referencing this RRN.
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
            <button type="button" onClick={onClose} className="px-5 py-2.5 border border-slate-300 text-sm font-bold uppercase">Cancel</button>
            <button data-testid="str-submit-btn" disabled={saving} type="submit"
              className="px-5 py-2.5 bg-[#F97316] text-white text-sm font-bold uppercase hover:bg-[#EA580C] disabled:opacity-50">
              {saving ? "Sending…" : "Send to Repair"}
            </button>
          </div>
        </form>
    </div>
  );
}

// ============== Mark Ready (receive repaired asset) Modal ==============
function MarkReadyModal({ asset, members, onClose, onSaved }) {
  const [assignType, setAssignType] = useState("Branch");
  const [clients, setClients] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [pickId, setPickId] = useState("");
  const [pickName, setPickName] = useState("");
  const [receivedDate, setReceivedDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/clients/picker"), api.get("/vendors/picker")]).then(([c, v]) => {
      setClients(c.data || []); setVendors(v.data || []);
    });
  }, []);

  const pool = assignType === "Member" ? members
            : assignType === "Client" ? clients
            : assignType === "Vendor" ? vendors : [];

  const codeOf = (x) => x.member_code || x.client_code || x.vendor_code || "";

  function resetPick() { setPickId(""); setPickName(""); }

  async function save(e) {
    e.preventDefault();
    if (assignType !== "Branch" && (!pickId || !pickName)) {
      toast.error(`Pick a ${assignType.toLowerCase()} from the master`); return;
    }
    setSaving(true);
    try {
      const body = { received_date: receivedDate, assign_type: assignType };
      if (assignType !== "Branch") { body.assign_id = pickId; body.assign_name = pickName; }
      await api.post(`/assets/${asset.id}/mark-ready`, body);
      if (assignType === "Branch") toast.success(`${asset.asset_code} → Available (branch stock)`);
      else toast.success(`${asset.asset_code} → Assigned to ${assignType} ${pickName}`);
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  }

  return (
    <div className="bg-white -mt-6 lg:-mt-10 -mx-4 sm:-mx-6 lg:-mx-10 min-h-[calc(100vh-38px)]" data-testid="mark-ready-modal">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-[38px] bg-white z-10 shadow-sm">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Receive Repaired Asset</div>
            <h2 className="font-display text-xl font-bold mt-0.5">{asset.asset_code} · {asset.name}</h2>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-4">
          <Field label="Received Date *">
            <input type="date" value={receivedDate} onChange={e => setReceivedDate(e.target.value)}
              className={inputCls} required data-testid="mr-received-date" />
          </Field>
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Where to assign? *</label>
            <div className="grid grid-cols-4 gap-2 mt-1.5">
              {["Branch", "Member", "Client", "Vendor"].map(t => (
                <button key={t} type="button" data-testid={`mr-assign-${t.toLowerCase()}`}
                  onClick={() => { setAssignType(t); resetPick(); }}
                  className={`py-2 text-xs uppercase tracking-wider font-bold border ${assignType === t ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-[#F97316]"}`}>
                  {t === "Branch" ? "Back to Stock" : t}
                </button>
              ))}
            </div>
          </div>
          {assignType !== "Branch" && (
            <Field label={`${assignType} *`}>
              <SearchableSelect
                key={assignType}
                testid="mr-assignee-search"
                value={pickId}
                onChange={(id, row) => { setPickId(id || ""); setPickName(row?.name || ""); }}
                items={pool}
                getLabel={(x) => `${codeOf(x) ? codeOf(x) + " — " : ""}${x.name}`}
                getSubLabel={(x) => assignType === "Member"
                  ? [x.designation, x.email].filter(Boolean).join(" · ")
                  : [x.gst_number, x.branch_name].filter(Boolean).join(" · ")}
                getSearchText={(x) => `${codeOf(x)} ${x.name || ""} ${x.designation || ""} ${x.email || ""} ${x.gst_number || ""}`}
                placeholder={`Select ${assignType.toLowerCase()} — search by code or name…`}
                searchPlaceholder={`Search ${pool.length} ${assignType.toLowerCase()}${pool.length === 1 ? "" : "s"} by code / name…`}
              />
              {pickId && <div className="text-[10px] text-emerald-700 mt-1">✓ Selected: <span className="font-bold">{codeOf(pool.find(x => x.id === pickId) || {})} — {pickName}</span></div>}
            </Field>
          )}
          <div className="bg-blue-50 border border-blue-200 px-3 py-2 text-[11px] text-blue-900">
            {assignType === "Branch"
              ? <>Asset goes back to <strong>Available</strong> in branch stock; it can be issued again via Issue Voucher.</>
              : <>Asset is set to <strong>Assigned</strong> to the selected {assignType.toLowerCase()}. Use Return Voucher to bring it back later.</>}
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
            <button type="button" onClick={onClose} className="px-5 py-2.5 border border-slate-300 text-sm font-bold uppercase">Cancel</button>
            <button data-testid="mr-submit-btn" disabled={saving} type="submit"
              className="px-5 py-2.5 bg-emerald-700 text-white text-sm font-bold uppercase hover:bg-emerald-800 disabled:opacity-50">
              {saving ? "Posting…" : "Mark Ready"}
            </button>
          </div>
        </form>
    </div>
  );
}

// ============== Asset Repair Report Modal ==============
function RepairReportModal({ asset, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/asset-repair-report/${asset.id}`).then(r => { setData(r.data); setLoading(false); })
      .catch(() => { toast.error("Could not load report"); setLoading(false); });
  }, [asset.id]);

  function downloadCSV() {
    if (!data) return;
    const rows = [
      ["RRN","Sent Date","Vendor","Reason","Cost (₹)","Received Date","PI Invoice","Status","Assigned After"],
      ...data.repairs.map(r => [
        r.voucher_number,
        (r.voucher_date || "").slice(0, 10),
        r.vendor_name || "",
        (r.description || "").replace(/\n/g, " "),
        r.repair_cost || 0,
        (r.repair_completed_at || "").slice(0, 10),
        r.linked_pi_number || "",
        r.current_status,
        r.post_repair_assign_name ? `${r.post_repair_assign_type}: ${r.post_repair_assign_name}` : (r.repair_completed_at ? "Branch stock" : "—"),
      ]),
    ];
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `repair_report_${asset.asset_code}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="bg-white -mt-6 lg:-mt-10 -mx-4 sm:-mx-6 lg:-mx-10 min-h-[calc(100vh-38px)]" data-testid="repair-report-modal">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-[38px] bg-white z-10 shadow-sm">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Asset History</div>
            <h2 className="font-display text-xl font-bold mt-0.5">{asset.asset_code} · {asset.name}</h2>
            <div className="text-[10px] text-slate-500 mt-0.5">{asset.make ? `${asset.make} · ` : ""}{asset.category} · {asset.branch_name || "—"}{asset.warehouse_name ? ` · ${asset.warehouse_name}` : ""}</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={downloadCSV} className="bg-white border border-slate-300 px-3 py-2 text-[10px] uppercase tracking-wider font-bold hover:bg-slate-50 flex items-center gap-1"><Download size={11} /> CSV</button>
            <button onClick={onClose}><X size={18} /></button>
          </div>
        </div>
        {loading ? <div className="p-12 text-center text-slate-400">Loading…</div> : !data ? null : (
          <div className="p-6 space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 text-sm bg-slate-50 px-4 py-3 border border-slate-200">
              <div><div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">Status</div><div className="font-bold text-[#F97316] mt-0.5">{data.asset.status}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">Total Repairs</div><div className="font-bold mt-0.5 tabular">{data.summary.total_repairs}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">Open</div><div className="font-bold text-amber-700 mt-0.5 tabular">{data.summary.open_repairs}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">Total Cost</div><div className="font-bold text-emerald-700 mt-0.5 tabular">{formatINR(data.summary.total_repair_cost)}</div></div>
            </div>

            <OwnershipTimeline asset={data.asset} movements={data.movements} />

            <div>
              <h3 className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">Repair History</h3>
              {data.repairs.length === 0 ? <div className="text-xs text-slate-400 italic py-6 text-center border border-slate-200">No repair history for this asset.</div> : (
                <div className="overflow-x-auto border border-slate-200">
                  <table className="w-full text-sm" data-testid="repair-report-table">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>{["RRN", "Sent", "Vendor", "Reason", "Cost", "Received", "PI Invoice", "Status", "Assigned After"].map(h =>
                        <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2.5 px-3">{h}</th>
                      )}</tr>
                    </thead>
                    <tbody>
                      {data.repairs.map(r => (
                        <tr key={r.id} className="border-b border-slate-100 align-top">
                          <td className="py-2.5 px-3 text-xs font-mono font-bold text-[#F97316]">{r.voucher_number}</td>
                          <td className="py-2.5 px-3 text-xs">{(r.voucher_date || "").slice(0, 10)}</td>
                          <td className="py-2.5 px-3 text-xs">{r.vendor_name || "—"}</td>
                          <td className="py-2.5 px-3 text-xs max-w-[260px]">{r.description || "—"}</td>
                          <td className="py-2.5 px-3 text-xs tabular font-bold">{formatINR(r.repair_cost || 0)}</td>
                          <td className="py-2.5 px-3 text-xs">{(r.repair_completed_at || "").slice(0, 10) || "—"}</td>
                          <td className="py-2.5 px-3 text-xs font-mono">{r.linked_pi_number || <span className="text-slate-400 italic">—</span>}</td>
                          <td className="py-2.5 px-3 text-[10px]">
                            <span className={`px-2 py-0.5 font-bold uppercase tracking-wider border ${r.current_status === "Completed" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : r.current_status === "In Repair" ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>
                              {r.current_status}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-xs">{r.post_repair_assign_name ? <span className="text-emerald-700"><b>{r.post_repair_assign_type}</b>: {r.post_repair_assign_name}</span> : r.repair_completed_at ? <span className="text-slate-500">Branch stock</span> : <span className="text-slate-400">—</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div>
              <h3 className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">Full Movement History</h3>
              {data.movements.length === 0 ? <div className="text-xs text-slate-400 italic py-6 text-center border border-slate-200">No movements recorded.</div> : (
                <div className="overflow-x-auto border border-slate-200">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>{["Date", "Type", "From", "To", "Reference", "Remarks", "By"].map(h =>
                        <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2.5 px-3">{h}</th>
                      )}</tr>
                    </thead>
                    <tbody>
                      {data.movements.map(m => (
                        <tr key={m.id} className="border-b border-slate-100">
                          <td className="py-2 px-3 text-xs">{(m.movement_date || "").slice(0, 10)}</td>
                          <td className="py-2 px-3 text-xs uppercase font-bold text-slate-700">{m.movement_type}</td>
                          <td className="py-2 px-3 text-xs">{m.from_member_name || "—"}</td>
                          <td className="py-2 px-3 text-xs">{m.to_member_name || m.to_location || "—"}</td>
                          <td className="py-2 px-3 text-xs font-mono text-slate-500">{m.reference_no || "—"}</td>
                          <td className="py-2 px-3 text-xs text-slate-600 max-w-[300px]">{m.remarks || "—"}</td>
                          <td className="py-2 px-3 text-[10px] text-slate-400">
                            <UserLabel value={m.created_by} fallback="—" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
    </div>
  );
}


// ============== Asset Ownership Timeline ==============
// Horizontal strip of past custody changes with type badges (Member / Client /
// Vendor / Branch / Repair / Scrap) and dates on the Asset detail view.
// Reads from /api/asset-repair-report/{id} which already returns the full
// `asset_movements` list sorted by date desc. We re-sort ascending (oldest →
// newest) so the visual flow reads left-to-right like a story.
const HOLDER_BADGE_STYLES = {
  Member:    { bg: "bg-sky-50",     border: "border-sky-300",     text: "text-sky-800",     dot: "bg-sky-500"     },
  Client:    { bg: "bg-violet-50",  border: "border-violet-300",  text: "text-violet-800",  dot: "bg-violet-500"  },
  Vendor:    { bg: "bg-amber-50",   border: "border-amber-300",   text: "text-amber-800",   dot: "bg-amber-500"   },
  Branch:    { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-800", dot: "bg-emerald-500" },
  Scrapped:  { bg: "bg-rose-50",    border: "border-rose-300",    text: "text-rose-800",    dot: "bg-rose-500"    },
  Unknown:   { bg: "bg-slate-50",   border: "border-slate-300",   text: "text-slate-700",   dot: "bg-slate-400"   },
};

// Movement types we treat as custody changes (everything that flips WHO holds
// the asset). REPAIR is included because the asset moves to a Vendor.
const CUSTODY_TYPES = new Set(["ASSIGN", "RETURN", "TRANSFER", "REPAIR", "READY", "SCRAP", "Assignment"]);

function _inferHolderType(m) {
  if (m.to_holder_type) return m.to_holder_type;
  const t = (m.movement_type || "").toUpperCase();
  if (t === "ASSIGNMENT" || t === "ASSIGN") return m.to_member_name ? "Member" : "Unknown";
  if (t === "RETURN" || t === "TRANSFER") return "Branch";
  if (t === "REPAIR") return "Vendor";
  if (t === "READY") return /branch\s*stock/i.test(m.remarks || "") ? "Branch" : (m.to_member_name ? "Member" : "Branch");
  if (t === "SCRAP") return "Scrapped";
  return "Unknown";
}

function _holderLabel(m) {
  return m.to_holder_name || m.to_member_name || m.to_location || (m.movement_type === "SCRAP" ? "Scrapped" : "—");
}

function _formatDate(d) {
  if (!d) return "—";
  const s = String(d).slice(0, 10);
  try {
    const dt = new Date(s + "T00:00:00");
    return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });
  } catch { return s; }
}

function OwnershipTimeline({ asset, movements }) {
  const custody = useMemo(() => {
    const filtered = (movements || []).filter(m => CUSTODY_TYPES.has(String(m.movement_type || "")));
    // sort oldest -> newest
    return [...filtered].sort((a, b) => String(a.movement_date || "").localeCompare(String(b.movement_date || "")));
  }, [movements]);

  // Compute the current holder so the rightmost "Now" marker mirrors the asset record itself.
  const nowHolder = useMemo(() => {
    if (asset?.status === "Scrapped") return { type: "Scrapped", name: "Scrapped" };
    if (asset?.held_by_type && asset?.held_by_name) return { type: asset.held_by_type, name: asset.held_by_name };
    if (asset?.assigned_to_member_name) return { type: "Member", name: asset.assigned_to_member_name };
    return { type: "Branch", name: asset?.branch_name || asset?.current_location || "Branch Stock" };
  }, [asset]);

  return (
    <div data-testid="asset-ownership-timeline">
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">
          Ownership Timeline · {custody.length} custody {custody.length === 1 ? "change" : "changes"}
        </h3>
        <div className="text-[10px] text-slate-400">Oldest → Latest · Hover any chip for the full audit line.</div>
      </div>

      {custody.length === 0 ? (
        <div className="text-xs text-slate-400 italic py-6 text-center border border-slate-200 bg-slate-50/40">
          No custody changes recorded yet — this asset has not been issued, transferred or repaired.
        </div>
      ) : (
        <div className="border border-slate-200 bg-gradient-to-b from-slate-50/60 to-white px-4 py-5 overflow-x-auto">
          <div className="relative inline-flex items-stretch gap-0 min-w-full">
            {/* the connecting rail */}
            <div className="absolute left-3 right-3 top-[26px] h-px bg-slate-300" aria-hidden />
            {custody.map((m, i) => {
              const type = _inferHolderType(m);
              const style = HOLDER_BADGE_STYLES[type] || HOLDER_BADGE_STYLES.Unknown;
              const label = _holderLabel(m);
              const ref = m.reference_no || "";
              const tip = [
                m.movement_type,
                ref && `· ${ref}`,
                `\n${type}: ${label}`,
                m.remarks && `\n${m.remarks}`,
                m.created_by && `\nby ${formatUser(m.created_by, m.created_by)}`,
              ].filter(Boolean).join(" ");
              return (
                <div key={m.id || i} className="relative flex flex-col items-center px-3 min-w-[160px] max-w-[200px]"
                     data-testid={`ownership-tl-step-${i}`}>
                  <div className={`relative z-10 w-3 h-3 rounded-full border-2 border-white ${style.dot} shadow-sm`} />
                  <div className="text-[10px] font-mono text-slate-500 mt-1.5">{_formatDate(m.movement_date)}</div>
                  <div className={`mt-1.5 inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold border ${style.bg} ${style.border} ${style.text}`}
                       title={tip}>
                    <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
                    {type}
                  </div>
                  <div className="mt-1.5 text-[11px] text-slate-700 font-medium text-center leading-tight line-clamp-2 break-words"
                       title={label}>
                    {label}
                  </div>
                  <div className="mt-0.5 text-[9px] uppercase tracking-wider text-slate-400 font-bold">
                    {m.movement_type}
                  </div>
                  {ref && (
                    <div className="text-[9px] font-mono text-slate-400 truncate max-w-full" title={ref}>{ref}</div>
                  )}
                </div>
              );
            })}
            {/* "Now" anchor mirroring the asset's current holder */}
            <div className="relative flex flex-col items-center px-3 min-w-[160px] max-w-[200px]"
                 data-testid="ownership-tl-now">
              <div className="relative z-10 w-3.5 h-3.5 rounded-full border-2 border-white bg-[#F97316] shadow ring-2 ring-[#F97316]/30" />
              <div className="text-[10px] font-mono text-[#F97316] font-bold mt-1.5">NOW</div>
              <div className={`mt-1.5 inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold border ${(HOLDER_BADGE_STYLES[nowHolder.type] || HOLDER_BADGE_STYLES.Unknown).bg} ${(HOLDER_BADGE_STYLES[nowHolder.type] || HOLDER_BADGE_STYLES.Unknown).border} ${(HOLDER_BADGE_STYLES[nowHolder.type] || HOLDER_BADGE_STYLES.Unknown).text}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${(HOLDER_BADGE_STYLES[nowHolder.type] || HOLDER_BADGE_STYLES.Unknown).dot}`} />
                {nowHolder.type}
              </div>
              <div className="mt-1.5 text-[11px] text-slate-900 font-bold text-center leading-tight line-clamp-2 break-words">
                {nowHolder.name}
              </div>
              <div className="mt-0.5 text-[9px] uppercase tracking-wider text-slate-500 font-bold">Current</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
